import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "component/auth/OnboardingLayout";
import PaymentStepper from "component/pay/PaymentStepper";
import {
    ArrowDown2,
    ArrowRight2,
    PercentageCircle,
    ReceiptDiscount,
    Timer1,
} from "iconsax-reactjs";
import { useSelector, useDispatch } from "react-redux";
import CurrencyToggle from "component/currencyToggle";
import { CurrencySelector } from "component/wallet/CurrencySelector";
import { formatMoney } from "utilities/formatMoney";
import { fetchFxRate } from "api/fetchers";
import { setCrossBorderProvider } from "redux/slices/crossBorderTransferSlice";
import SelectBankProviderModal from "component/wallet/SelectBankProviderModal";
import useDebouncedValue from "hooks/useDebouncedValue";

const INPUT_DEBOUNCE_MS = 600;

const CrossBorderTradevuUser = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // From store
    const { business, currency: currentCurrency } = useSelector((s) => s);
    const wallets = useSelector((s) => s.wallets || []);
    const isBusinessApproved = business?.status === "APPROVED";
    const businessId = business?._id;

    // Local state
    const [amount, setAmount] = useState("");
    const [toCurrency, setToCurrency] = useState("");
    const [rate, setRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    const [rateError, setRateError] = useState(null);
    const [provider, setProviderState] = useState(null);
    const [showProviderModal, setShowProviderModal] = useState(false);

    const flatFee = 1500;
    const minAmount = 10000;

    const currentWallet = useMemo(
        () =>
            wallets.find(
                (w) => (w.currency || "").toUpperCase() === String(currentCurrency).toUpperCase()
            ),
        [wallets, currentCurrency]
    );
    const isKES = String(currentCurrency || "").toUpperCase() === "KES";
    const providers = useMemo(
        () => (Array.isArray(currentWallet?.providers) ? currentWallet.providers : []),
        [currentWallet]
    );
    const hasMultipleProviders = providers.length > 1 && !isKES;
    const needsProvider = hasMultipleProviders && !provider;

    useEffect(() => {
        setProviderState(null);
    }, [currentCurrency]);

    const selectedProviderBalance = useMemo(() => {
        if (!hasMultipleProviders || !provider) return null;
        const value = Number(provider?.balance);
        return Number.isFinite(value) ? value : null;
    }, [hasMultipleProviders, provider]);

    const balance =
        selectedProviderBalance !== null
            ? selectedProviderBalance
            : Number(currentWallet?.balance || 0);

    useEffect(() => {
        if (toCurrency) return;
        const firstOther = wallets.find(
            (w) => (w.currency || "").toUpperCase() !== String(currentCurrency).toUpperCase()
        );
        if (firstOther?.currency) setToCurrency(firstOther.currency);
    }, [wallets, currentCurrency, toCurrency]);

    const numericAmount = Number.parseFloat(amount || "0") || 0;
    const debouncedNumericAmount = useDebouncedValue(numericAmount, INPUT_DEBOUNCE_MS);
    const isAmountSettling = numericAmount !== debouncedNumericAmount;
    const feeValue = flatFee;
    const insufficientFunds = numericAmount + feeValue > balance;
    const hasValidAmount = numericAmount >= minAmount && !insufficientFunds;

    // Fetch FX rate (cross-border)
    useEffect(() => {
        const from = (currentCurrency || "").toUpperCase();
        const to = (toCurrency || "").toUpperCase();
        if (!businessId || !from || !to || debouncedNumericAmount <= 0) {
            setRate(null);
            setRateError(null);
            return;
        }

        let cancelled = false;
        (async () => {
            setRateLoading(true);
            setRateError(null);
            try {
                const r = await fetchFxRate({
                    fromCurrency: from,
                    toCurrency: to,
                    amount: Number.isFinite(Number(debouncedNumericAmount))
                        ? Number(debouncedNumericAmount)
                        : 0,
                    businessId,
                });
                const roundedRate = typeof r === "number" ? r : Number(r?.roundedRate);
                if (!cancelled) {
                    if (!Number.isFinite(roundedRate) || roundedRate <= 0) {
                        throw new Error("Invalid FX quote returned");
                    }
                    setRate(roundedRate);
                }
            } catch (e) {
                if (!cancelled) {
                    setRate(null);
                    setRateError(e.message || "Failed to fetch FX rate");
                }
            } finally {
                if (!cancelled) setRateLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentCurrency, toCurrency, debouncedNumericAmount, businessId]);

    const isRateReady =
        !isAmountSettling &&
        !rateLoading &&
        typeof rate === "number" &&
        Number.isFinite(rate) &&
        rate > 0;

    const convertedAmount = useMemo(() => {
        if (!hasValidAmount || !isRateReady) return 0;
        const net = Math.max(0, numericAmount - feeValue);
        return net * Number(rate || 0);
    }, [numericAmount, feeValue, rate, hasValidAmount, isRateReady]);

    const canContinue = hasValidAmount && Boolean(toCurrency) && isRateReady && !needsProvider;

    const onContinue = () => {
        dispatch(setCrossBorderProvider(provider));
        navigate("/payment/send-money/cross-border/tradevu-user/recipient-info");
    };

    return (
        <OnboardingLayout title="Send to bank" showSkip={false}>
            <div className="max-w-2xl mx-auto mt-10 space-y-10 md:px-4 pb-32 md:pb-0">
                <PaymentStepper currentStep={0} />
                <h2 className="text-2xl font-normal">How much do you want to send?</h2>

                <div className="bg-white rounded-xl p-6 max-w-3xl w-full">
                    {/* Top row: label + CurrencyToggle */}
                    <div className="border-b pb-4 mb-4">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-[14px] font-[450] text-[#383B56]">You send</span>
                            <CurrencyToggle
                                className="w-full text-sm bg-transparent"
                                dropDownClassName="shadow-dropdown2 right-0 !top-[109%]"
                                fetchWallet
                                businessId={businessId}
                                isDisabled={!isBusinessApproved}
                                hideLabel
                            />
                        </div>

                        {/* Amount input */}
                        <div
                            className={`flex items-start text-7xl font-normal leading-none ${
                                insufficientFunds ? "text-red-600" : ""
                            }`}
                        >
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className={`!shadow-none outline-none font-thin w-full caret-[#6F00FF] ${
                                    insufficientFunds ? "border-b border-red-500" : ""
                                }`}
                                placeholder="0"
                                aria-invalid={insufficientFunds}
                                aria-describedby={insufficientFunds ? "amount-error" : undefined}
                                min="0"
                            />
                        </div>

                        <div
                            className={`flex ${
                                insufficientFunds ? "justify-between" : "justify-end items-start"
                            }`}
                        >
                            {insufficientFunds && (
                                <div
                                    id="amount-error"
                                    role="alert"
                                    className="text-red-600 text-sm"
                                >
                                    Insufficient balance. Available: {balance.toLocaleString()}{" "}
                                    {currentCurrency}
                                </div>
                            )}
                            <div className="text-[#6B7280] text-sm">
                                Balance: {balance.toLocaleString()} {currentCurrency}
                            </div>
                        </div>
                    </div>

                    {/* Fee + Rate block */}
                    <div className="relative my-6">
                        <div className="absolute top-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute bottom-0 left-0 right-0 h-px bg-[#E1E3F4]" />
                        <div className="absolute left-4 top-[0px] bottom-[0px] w-px bg-[#E1E3F4] z-0" />
                        <span className="absolute left-[9px] top-[111px]">
                            <ArrowDown2 size={16} color="#E1E3F4" />
                        </span>

                        <div className="py-4 space-y-4">
                            {hasMultipleProviders && (
                                <div className="flex items-start gap-3 relative z-10">
                                    <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                        <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                    </div>
                                    <div className="flex mt-1.5 justify-between items-center w-full">
                                        <span className="text-[#161827] text-[16px] font-[450]">
                                            {provider ? (
                                                <>
                                                    Sending via{" "}
                                                    {provider.bankName || provider.bank || "—"} (
                                                    {provider.accountNumber || "—"})
                                                </>
                                            ) : (
                                                "No bank account selected"
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setShowProviderModal(true)}
                                            className="text-[#101828] inline-flex items-center gap-1"
                                            aria-haspopup="dialog"
                                        >
                                            <span className="text-[#6F00FF] font-[450] text-[16px]">
                                                {provider ? "Change bank" : "Select bank"}
                                            </span>
                                            <ArrowRight2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <ReceiptDiscount size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Fee</span>
                                    <span>
                                        {formatMoney(feeValue)}{" "}
                                        {String(currentCurrency).toUpperCase()}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start gap-3 relative z-10">
                                <div className="w-8 h-8 rounded-full bg-[#F3F4FB] flex items-center justify-center">
                                    <PercentageCircle size={16} color="#6F739B" variant="Bulk" />
                                </div>
                                <div className="flex mt-1.5 justify-between items-center w-full text-sm text-[#6F6B7B]">
                                    <span>Rate</span>
                                    <span>
                                        {!toCurrency ? (
                                            "Select a currency"
                                        ) : rateLoading ? (
                                            "Fetching rate…"
                                        ) : rateError ? (
                                            <span className="text-red-600" role="alert">
                                                {rateError}
                                            </span>
                                        ) : (
                                            `1 ${String(
                                                currentCurrency
                                            ).toUpperCase()} = ${rate} ${String(
                                                toCurrency
                                            ).toUpperCase()}`
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recipient gets + CurrencySelector */}
                    <div className="pt-4 border-t">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-500">Recipient gets</span>
                            <CurrencySelector
                                wallets={wallets.filter(
                                    (w) =>
                                        (w.currency || "").toUpperCase() !==
                                        String(currentCurrency).toUpperCase()
                                )}
                                selected={toCurrency}
                                onSelect={(code) => setToCurrency(code)}
                                currencyFrom={String(currentCurrency || "").toUpperCase()}
                            />
                        </div>

                        <div className="flex items-start text-7xl font-thin leading-none">
                            <span>{formatMoney(convertedAmount)}</span>
                        </div>
                    </div>
                </div>

                {/* Desktop / tablet footer */}
                <div className="max-w-2xl mx-auto mb-5 hidden md:block">
                    <div className="flex justify-between items-center border-t border-[#E5E7EB]">
                        <div className="flex items-center gap-2 text-[#6B7280] text-sm">
                            <Timer1 size="22" color="#6F739B" />
                            <span>Arrives in minutes</span>
                        </div>

                        <div className="flex items-center gap-4">
                            <button
                                className="text-[#111827] text-[16px] font-[450]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!canContinue}
                                className={`rounded-xl px-6 py-2 text-white font-semibold text-[16px] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF] hover:bg-[#6A1BFF]"
                                }`}
                                onClick={onContinue}
                                title={
                                    needsProvider
                                        ? "Select a bank account"
                                        : !toCurrency
                                        ? "Select a currency"
                                        : rateLoading
                                          ? "Waiting for rate…"
                                          : !hasValidAmount
                                            ? "Enter a valid amount"
                                            : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile sticky footer */}
                <div className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#F6F7FD] border-t border-[#E7E9FB]">
                    <div
                        className="max-w-2xl mx-auto px-4 pt-2.5 pb-3"
                        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}
                    >
                        <div className="flex items-center justify-center mb-2 text-[#161827]">
                            <Timer1 size="22" color="#6F739B" />
                            <span className="ml-2 text:[15px] font-[450]">Arrives in minutes</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <button
                                className="text-[#111827] text-[16px] font-[450] w-[48%]"
                                onClick={() => navigate(-1)}
                            >
                                Back
                            </button>
                            <button
                                disabled={!canContinue}
                                onClick={onContinue}
                                className={`h-12 rounded-xl px-5 font-semibold text-white text-[16px] w-[48%] ${
                                    !canContinue
                                        ? "bg-[#E5DFFF] cursor-not-allowed"
                                        : "bg-[#7A27FF]"
                                }`}
                                title={
                                    needsProvider
                                        ? "Select a bank account"
                                        : !toCurrency
                                        ? "Select a currency"
                                        : rateLoading
                                          ? "Waiting for rate…"
                                          : !hasValidAmount
                                            ? "Enter a valid amount"
                                            : ""
                                }
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <SelectBankProviderModal
                isOpen={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onSelect={(p) => setProviderState(p)}
                selectedId={provider?._id || provider?.accountNumber}
                providers={providers}
                currency={currentCurrency}
            />
        </OnboardingLayout>
    );
};

export default CrossBorderTradevuUser;
